import type { DataGridProps } from '@cmg/data-grid';
import { ServerDataGrid, useDataGridContext } from '@cmg/data-grid';
import { Box } from '@cmg/design-system';
import { useMemo } from 'react';

import { useRefetchOnReactiveVar } from '../../../../../../../common/hooks/useRefetchOnReactiveVar/useRefetchOnReactiveVar';
import { firmsInvestorContactsGridColumns } from './columns';
import type { FirmsInvestorContactsGridContext } from './FirmsInvestorContactsGrid.model';
import {
  INITIAL_INVESTOR_CONTACTS_GRID_STATE,
  refetchFirmInvestorContactsGridReactiveVar,
} from './FirmsInvestorContactsGrid.model';
import type { FirmInvestorContactsGridItemFragment } from './graphql/__generated__';
import { useFirmsInvestorContactsGridDataSource } from './hooks/useFirmsInvestorContactsGridDataSource';

const AUTO_SIZE_STRATEGY: DataGridProps<FirmInvestorContactsGridItemFragment>['autoSizeStrategy'] =
  {
    type: 'fitGridWidth',
  };

export type FirmsInvestorContactsGridProps = Readonly<{
  tenantId?: string;
  firmId: string;
  onEditInvestorContact: (contact: FirmInvestorContactsGridItemFragment) => void;
}>;

export function FirmsInvestorContactsGrid({
  tenantId,
  firmId,
  onEditInvestorContact,
}: FirmsInvestorContactsGridProps) {
  const dataSource = useFirmsInvestorContactsGridDataSource({ tenantId, firmId });
  const { refreshServerSide } = useDataGridContext();

  useRefetchOnReactiveVar({
    reactiveVar: refetchFirmInvestorContactsGridReactiveVar,
    onRefetch: refreshServerSide,
  });

  const context: FirmsInvestorContactsGridContext = useMemo(() => {
    return {
      onEditInvestorContact,
    };
  }, [onEditInvestorContact]);

  return (
    <Box data-testid="investor-contacts-section" height="100%">
      <ServerDataGrid<FirmInvestorContactsGridItemFragment, FirmsInvestorContactsGridContext>
        serverSideDatasource={dataSource}
        columnDefs={firmsInvestorContactsGridColumns}
        autoSizeStrategy={AUTO_SIZE_STRATEGY}
        context={context}
        initialState={INITIAL_INVESTOR_CONTACTS_GRID_STATE}
      />
    </Box>
  );
}
